/**
 * =============================================================================
 * Hook: useAffiliateMembershipActions
 * Versão: 1.0.0
 *
 * Descrição:
 *   Hook para ações administrativas sobre uma adesão (membership) específica
 *   de um afiliado em um programa, no modelo N:N (Uma pessoa, vários
 *   programas). Cada ação é uma chamada server-side a uma RPC dedicada que
 *   já valida permissão (root_admin / tenant_admin) e tenant ativo.
 *
 *   Ações disponíveis:
 *     - approveMembership(id)
 *     - rejectMembership(id, reason?)
 *     - suspendMembership(id, reason?)
 *     - reactivateMembership(id)
 *
 * Release notes:
 *   v1.0.0 - Criação do hook (Fase 2.5 do roadmap N:N de afiliados).
 * =============================================================================
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';

// Logger centralizado
const logger = createLogger('AffiliateMembershipActions');

// Tipo de ação (apenas para logging/diagnóstico)
type MembershipAction = 'approve' | 'reject' | 'suspend' | 'reactivate';

interface MembershipMutationVars {
  membershipId: string;
  reason?: string;
}

export function useAffiliateMembershipActions() {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();

  /**
   * Invalida caches relacionados a afiliados/memberships após qualquer ação.
   * Mantém a UI sincronizada com o servidor sem refetch manual.
   */
  const invalidateAffiliateCaches = () => {
    queryClient.invalidateQueries({
      queryKey: ['referral-affiliates-paginated', currentTenant?.id],
    });
    queryClient.invalidateQueries({ queryKey: ['referral-affiliates'] });
    queryClient.invalidateQueries({ queryKey: ['referral-memberships'] });
    // v1.0.1 — também invalida a query do drawer de detalhes do afiliado
    // (`useReferralAffiliate`) para manter o status agregado em sincronia
    // com a adesão recém-moderada.
    queryClient.invalidateQueries({ queryKey: ['referral-affiliate'] });
  };

  /**
   * Wrapper genérico para execução de RPCs de ação em membership.
   * Centraliza tratamento de erro, toast e invalidação de cache.
   */
  const runMembershipRpc = async (
    action: MembershipAction,
    rpcName: string,
    params: Record<string, unknown>,
  ) => {
    logger.debug(`Executando ação "${action}" em membership`, params);
    // Cast necessário até regenerar types.ts com as novas RPCs
    const { error } = await (supabase.rpc as any)(rpcName, params);
    if (error) {
      logger.error(`Falha em ${rpcName}`, error);
      throw error;
    }
  };

  // ---------------------------------------------------------------------------
  // Mutations
  // ---------------------------------------------------------------------------

  const approveMutation = useMutation({
    mutationFn: ({ membershipId }: MembershipMutationVars) =>
      runMembershipRpc('approve', 'approve_affiliate_membership', {
        p_membership_id: membershipId,
        p_tenant_hint: currentTenant?.id || null,
      }),
    onSuccess: () => {
      toast.success('Adesão aprovada com sucesso');
      invalidateAffiliateCaches();
    },
    onError: (err: any) => {
      toast.error(err?.message || 'Erro ao aprovar adesão');
    },
  });

  const rejectMutation = useMutation({
    mutationFn: ({ membershipId, reason }: MembershipMutationVars) =>
      runMembershipRpc('reject', 'reject_affiliate_membership', {
        p_membership_id: membershipId,
        p_reason: reason || null,
        p_tenant_hint: currentTenant?.id || null,
      }),
    onSuccess: () => {
      toast.success('Adesão rejeitada');
      invalidateAffiliateCaches();
    },
    onError: (err: any) => {
      toast.error(err?.message || 'Erro ao rejeitar adesão');
    },
  });

  const suspendMutation = useMutation({
    mutationFn: ({ membershipId, reason }: MembershipMutationVars) =>
      runMembershipRpc('suspend', 'suspend_affiliate_membership', {
        p_membership_id: membershipId,
        p_reason: reason || null,
        p_tenant_hint: currentTenant?.id || null,
      }),
    onSuccess: () => {
      toast.success('Adesão suspensa');
      invalidateAffiliateCaches();
    },
    onError: (err: any) => {
      toast.error(err?.message || 'Erro ao suspender adesão');
    },
  });

  const reactivateMutation = useMutation({
    mutationFn: ({ membershipId }: MembershipMutationVars) =>
      runMembershipRpc('reactivate', 'reactivate_affiliate_membership', {
        p_membership_id: membershipId,
        p_tenant_hint: currentTenant?.id || null,
      }),
    onSuccess: () => {
      toast.success('Adesão reativada');
      invalidateAffiliateCaches();
    },
    onError: (err: any) => {
      toast.error(err?.message || 'Erro ao reativar adesão');
    },
  });

  // ---------------------------------------------------------------------------
  // API pública do hook
  // ---------------------------------------------------------------------------
  return {
    approveMembership: (membershipId: string) =>
      approveMutation.mutateAsync({ membershipId }),
    rejectMembership: (membershipId: string, reason?: string) =>
      rejectMutation.mutateAsync({ membershipId, reason }),
    suspendMembership: (membershipId: string, reason?: string) =>
      suspendMutation.mutateAsync({ membershipId, reason }),
    reactivateMembership: (membershipId: string) =>
      reactivateMutation.mutateAsync({ membershipId }),

    isApproving: approveMutation.isPending,
    isRejecting: rejectMutation.isPending,
    isSuspending: suspendMutation.isPending,
    isReactivating: reactivateMutation.isPending,
    isLoading:
      approveMutation.isPending ||
      rejectMutation.isPending ||
      suspendMutation.isPending ||
      reactivateMutation.isPending,
  };
}
